import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) return this.onLineItemRemove(line);

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const cartPerformanceMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // Build sections payload (same as before)
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponse = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponse.errors) {
          this.#handleCartError(line, parsedResponse);
          return;
        }

        const parser = new DOMParser();
        const newSectionHTML = parser.parseFromString(parsedResponse.sections[this.sectionId], 'text/html');

        // Get rows using your table row class
        const newRows = Array.from(newSectionHTML.querySelectorAll('.cart-items__table-row'));
        const currentRows = Array.from(this.querySelectorAll('.cart-items__table-row'));

        // ---------- If cart is now empty: do a full morph to render the theme's empty state ----------
        if (newRows.length === 0) {
          // Replace the whole section so empty-cart markup & summary are correct
          morphSection(this.sectionId, parsedResponse.sections[this.sectionId]);

          // Ensure bubble and totals cleared
          const cartBubbleCount = document.querySelector('[ref="cartBubbleCount"]');
          if (cartBubbleCount) cartBubbleCount.textContent = '0';
          const cartTotalRef = this.refs.cartTotal;
          if (cartTotalRef) cartTotalRef.innerHTML = '';

          // Dispatch event to notify other components
          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: 0,
              source: 'cart-items-component',
              sections: parsedResponse.sections,
            })
          );

          return; // done
        }

        // ---------- Normal case: patch rows and totals, keep delivery-info untouched ----------
        // Replace/patch rows
        newRows.forEach((newRow, index) => {
          if (currentRows[index]) {
            currentRows[index].innerHTML = newRow.innerHTML;

            // Ensure product total cell updates (handles theme text-component quirks)
            const newProductTotal = newRow.querySelector('[headers="productTotal"], .cart-items__price');
            const currentProductTotal = currentRows[index].querySelector('[headers="productTotal"], .cart-items__price');
            if (newProductTotal && currentProductTotal) {
              currentProductTotal.innerHTML = newProductTotal.innerHTML;
            }
          } else {
            // If new row exists but current does not, append it to tbody
            const tbody = this.querySelector('tbody');
            if (tbody) {
              tbody.appendChild(newRow.cloneNode(true));
            }
          }
        });

        // Remove any leftover old rows (when quantity removal reduces rows)
        if (currentRows.length > newRows.length) {
          for (let i = newRows.length; i < currentRows.length; i++) {
            currentRows[i].remove();
          }
        }

        // Update cart total block
        const newCartTotal = newSectionHTML.querySelector('[ref="cartTotal"]');
        if (cartTotal && newCartTotal) cartTotal.innerHTML = newCartTotal.innerHTML;

        // Update cart bubble count (try local first, fallback to global query)
        const cartBubbleCountLocal = this.querySelector('[ref="cartBubbleCount"]');
        const cartBubbleCountGlobal = document.querySelector('[ref="cartBubbleCount"]');
        const newCartBubbleCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent || '0';
        if (cartBubbleCountLocal) cartBubbleCountLocal.textContent = newCartBubbleCount;
        if (!cartBubbleCountLocal && cartBubbleCountGlobal) cartBubbleCountGlobal.textContent = newCartBubbleCount;

        // Dispatch event with updated item count
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponse.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformanceMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponse) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponse.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const parser = new DOMParser();
      const newSectionHTML = parser.parseFromString(cartItemsHtml, 'text/html');

      const newRows = Array.from(newSectionHTML.querySelectorAll('.cart-items__table-row'));
      const currentRows = Array.from(this.querySelectorAll('.cart-items__table-row'));

      // If empty => full morph so empty-cart template & summary are correct
      if (newRows.length === 0) {
        morphSection(this.sectionId, cartItemsHtml);

        const cartBubbleCount = document.querySelector('[ref="cartBubbleCount"]');
        if (cartBubbleCount) cartBubbleCount.textContent = '0';
        const cartTotalRef = this.refs.cartTotal;
        if (cartTotalRef) cartTotalRef.innerHTML = '';

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: 0,
            source: 'cart-items-component',
            sections: event.detail.data.sections,
          })
        );
        return;
      }

      // Normal patching flow (rows, totals, bubble)
      newRows.forEach((newRow, index) => {
        if (currentRows[index]) {
          currentRows[index].innerHTML = newRow.innerHTML;

          const newProductTotal = newRow.querySelector('[headers="productTotal"], .cart-items__price');
          const currentProductTotal = currentRows[index].querySelector('[headers="productTotal"], .cart-items__price');
          if (newProductTotal && currentProductTotal) {
            currentProductTotal.innerHTML = newProductTotal.innerHTML;
          }
        } else {
          const tbody = this.querySelector('tbody');
          if (tbody) tbody.appendChild(newRow.cloneNode(true));
        }
      });

      if (currentRows.length > newRows.length) {
        for (let i = newRows.length; i < currentRows.length; i++) currentRows[i].remove();
      }

      const cartTotal = this.refs.cartTotal;
      const newCartTotal = newSectionHTML.querySelector('[ref="cartTotal"]');
      if (cartTotal && newCartTotal) cartTotal.innerHTML = newCartTotal.innerHTML;

      const cartBubbleCount = this.querySelector('[ref="cartBubbleCount"]') || document.querySelector('[ref="cartBubbleCount"]');
      const newCartBubbleCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent || '0';
      if (cartBubbleCount) cartBubbleCount.textContent = newCartBubbleCount;
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
